<script lang="ts" setup>
  import { parseInline } from 'marked'

  import { BngContentFit, BngPageFooter } from '~/layouts/components'

  const { t } = useI18n()
  const { matches: matchDesktop } = useMatchMedia(ScreenWidth.desktop)

  useHead({
    title: 'Bookandgolf | Propriétaire d\'un golf',
    meta: [
      {
        name: 'description',
        content: 'Rejoignez Bookandgolf, proposez la vente de vos green fees en ligne',
      },
    ],
  })
</script>
<template>
  <div class="p-b2b">
    <StaticPageHeader
      :illustration="`/illustrations/illus-page-b2b-${ matchDesktop ? 'desk' : 'mobile'}.png`"
      :title-top="parseInline(t('b2b.title-top'), {breaks: true, async: false})"
      :title-main="parseInline(t('b2b.title-main'), {breaks: true, async: false})"
      :title-bottom="parseInline(t('b2b.title-bottom'), {breaks: true, async: false})"
      :bubble-title="parseInline(t('b2b.bubble-title'), {breaks: true, async: false})"
      :bubble-text="parseInline(t('b2b.bubble-text'), {breaks: true, async: false})"
    />
    <div class="cards">
      <StaticPageCard
        icon="mate"
        :title="t('b2b.card-1-title')"
        :text="t('b2b.card-1-text')"
      >
        <div class="values-subtitle">
          <div class="value-subtitle">
            <div class="value">
              <span>2011</span>
            </div>
            <div class="subtitle">
              <span>{{ t("b2b.card-1-subtitle-1") }}</span>
            </div>
          </div>
          <div class="value-subtitle">
            <div class="value">
              <span>90 000</span>
            </div>
            <div class="subtitle">
              <span>{{ t("b2b.card-1-subtitle-2") }}</span>
            </div>
          </div>
          <div class="value-subtitle">
            <div class="top">
              <span>{{ t("b2b.more-than") }}</span>
            </div>
            <div class="value">
              <span>100 000</span>
            </div>
            <div class="subtitle">
              <span>{{ t("b2b.card-1-subtitle-3") }}</span>
            </div>
          </div>
          <div class="value-subtitle">
            <div class="top">
              <span>{{ t("b2b.more-than") }}</span>
            </div>
            <div class="value">
              <span>150</span>
            </div>
            <div class="subtitle">
              <span>{{ t("b2b.card-1-subtitle-4") }}</span>
            </div>
          </div>
        </div>
      </StaticPageCard>
      <StaticPageCard
        icon="gear"
        :title="t('b2b.card-2-title')"
        :text="t('b2b.card-2-text')"
      >
        <div
          class="rich-text"
          v-html="t('b2b.card-2-rich-text')"
        />
        <div class="icons-subtitle">
          <div
            v-for="item in [
              'albatros',
              'chronogolf',
              'cps',
              'memgolf',
              'netgolf',
              'prima',
            ]"
            :key="item"
            class="icon-subtitle"
          >
            <img
              :alt="item"
              class="icon"
              :src="`logos/${item}.png`"
            >
            <div class="subtitle">
              <span>{{ item }}</span>
            </div>
          </div>
        </div>
      </StaticPageCard>
    </div>
    <ActionBlock
      title-icon="kady@2x"
      :title="t('b2b.action-block-title')"
    >
      <span class="action-block-text">{{ t("b2b.action-block-text") }}</span>
    </ActionBlock>
    <BngContentFit>
      <div class="cards-3">
        <StaticPageCardAlt icon="benefits">
          <div class="card-3">
            <div class="section">
              <div class="head">
                <SvgIcon
                  icon="illustration-golf-player"
                  :width="100"
                  :height="85"
                />
                <div
                  class="subtitle"
                  v-html="t('b2b.card-3-1-subtitle')"
                />
              </div>
              <div class="columns">
                <p class="text">
                  <span v-html="t('b2b.card-3-1-text-1')" />
                </p>
                <p class="text">
                  <span v-html="t('b2b.card-3-1-text-2')" />
                </p>
                <p class="text">
                  <span v-html="t('b2b.card-3-1-text-3')" />
                </p>
              </div>
            </div>
          </div>
        </StaticPageCardAlt>
        <StaticPageCardAlt icon="benefits">
          <div class="card-3">
            <div class="section">
              <div class="head">
                <SvgIcon
                  icon="illustration-golf-player-wallet"
                  :width="100"
                  :height="85"
                />
                <div
                  class="subtitle"
                  v-html="t('b2b.card-3-2-subtitle')"
                />
              </div>
              <div class="columns">
                <p class="text">
                  <span v-html="t('b2b.card-3-2-text-1')" />
                </p>
                <p class="text">
                  <span v-html="t('b2b.card-3-2-text-2')" />
                </p>
                <p class="text">
                  <span v-html="t('b2b.card-3-2-text-3')" />
                </p>
              </div>
            </div>
          </div>
        </StaticPageCardAlt>
      </div>
    </BngContentFit>
    <span class="quote">
      Avec Bookandgolf,<strong> vous maîtrisez vos offres à 100%, </strong>alors pourquoi pas essayer
      ?</span>
    <ContactBlock no-faq />
    <BngPageFooter />
  </div>
</template>
<style scoped>
.p-b2b {
  background-color: white;

  .cards {
    display: flex;
    flex-wrap: wrap;
  }

  .c-action-block {
    justify-content: space-between;
    color: var(--color-secondary);
    font-family: Open-Sans, sans-serif;
    font-size: 1.125em;
    font-weight: 600;

    .action-block-text {
      position: relative;
      max-width: 25em;
      margin-top: 2em;
      color: var(--color-secondary);
      font-family: Open-Sans, sans-serif;
      font-size: 1em;
      font-weight: 600;
      line-height: 1.5;
      opacity: 0.8;
    }
  }

  .c-static-page-card {
    .values-subtitle {
      display: flex;
      flex-flow: row wrap;

      .value-subtitle {
        display: flex;
        width: 50%;
        flex-direction: column;
        align-items: center;
        margin-top: 2em;

        .top {
          span {
            color: var(--color-secondary);
            font-family: Bariol, sans-serif;
            font-size: 1.625em;
            font-weight: 700;
          }
        }

        .value {
          span {
            color: var(--color-secondary);
            font-family: Bariol, sans-serif;
            font-size: 2em;
            font-weight: bold;
          }
        }

        .subtitle {
          padding: 0 1rem;
          margin-top: 0.75em;
          text-align: center;

          span {
            color: var(--color-primary);
            font-family: Bariol, sans-serif;
            font-size: 1.125em;
            font-weight: 600;
          }
        }
      }
    }

    .rich-text {
      margin-top: 1rem;
      color: var(--color-secondary);
      font-family: Open-Sans, sans-serif;
      font-size: 0.875em;
      font-weight: 400;
      line-height: 1.5;
    }

    .icons-subtitle {
      display: flex;
      flex-flow: row wrap;
      margin-top: 1rem;

      .icon-subtitle {
        display: flex;
        width: 33%;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        margin-top: 2rem;

        .icon {
          width: 3rem;
          height: 3rem;
        }

        .subtitle {
          margin-top: 0.5em;
          text-align: center;
          text-transform: capitalize;

          span {
            color: var(--color-secondary);
            font-family: Open-Sans, sans-serif;
            font-size: 0.875em;
            font-weight: 600;
          }
        }
      }
    }
  }

  .cards-3 {
    padding: 2em 0 0;

    .c-static-page-card {
      max-width: none;

      .card-3 {
        display: flex;
        min-width: 0;
        flex: 1 1 auto;
        justify-content: space-evenly;
        text-align: center;

        .section {
          width: 100%;

          .head {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: flex-start;
            padding-top: 1.5em;
            padding-left: 0;

            .subtitle {
              color: var(--color-secondary);
              font-family: Bariol, sans-serif;
              font-size: 2em;
              font-weight: 700;
            }
          }

          .columns {
            display: flex;
            flex-direction: column;
            margin-top: 2em;

            .text {
              position: relative;
              display: flex;
              flex: 1 1 auto;
              align-items: center;
              justify-content: center;
              padding: 1.5em 0;
              line-height: 1.5;

              &:first-child {
                padding-top: 0;
              }

              &:last-child {
                padding-bottom: 0;

                &::after {
                  display: none;
                }
              }

              &::after {
                position: absolute;
                bottom: -5px;
                left: calc(50% + 5px);
                width: 10px;
                height: 10px;
                border-radius: 50%;
                background-color: var(--color-primary);
                content: "";
              }

              span {
                color: var(--color-secondary);
                font-family: Open-Sans, sans-serif;
                font-size: 1.125em;
                font-weight: 600;
              }
            }
          }
        }
      }
    }
  }

  .quote {
    display: block;
    max-width: 35rem;
    margin: 5em auto;
    color: var(--color-secondary);
    font-family: Bariol, sans-serif;
    font-size: 2em;
    font-weight: 700;
    text-align: center;

    strong {
      color: var(--color-primary);
    }
  }

  @media screen and (width >= 1024px) {
    .action-block-text {
      font-size: 1.25em;
    }

    .c-static-page-card {
      position: relative;
      display: flex;
      width: 100%;
      align-items: center;
      justify-content: center;

      .c-paper {
        padding: 2rem;
      }

      .rich-text {
        font-size: 1.125em;
        line-height: 1.5;
      }
    }

    .cards {
      display: flex;
      flex-direction: row;
      justify-content: center;

      > .c-static-page-card {
        display: flex;
        max-width: 34rem;
        flex: 1 0 auto;
        margin: 4em;

        .c-paper {
          padding: 2em;
        }

        .text {
          margin: auto 0;
        }
      }
    }

    .cards-3 {
      padding: 4em 4em 0;

      .c-static-page-card {
        .card-3 {
          .section {
            .head {
              flex-direction: row;
              padding-top: 0;
              padding-left: 5em;

              .subtitle {
                margin-left: 2rem;
              }
            }

            .columns {
              flex-direction: row;

              .text {
                padding: 0 1.5em;

                &:first-child {
                  padding-left: 0;
                }

                &:last-child {
                  padding-right: 0;
                }

                &::after {
                  inset: calc(50% - 5px) -5px auto auto;
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>
